import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LogoIcon } from '../../../../assets/icons/logo-squarebit.svg';
import { BREAKPOINTS, ROUTER_LINKS } from '../../consts/app-keys.const';
import useAuth from '../../../auth/feature/auth';
import { HeaderButtons } from './header-buttons.component';
import { HeaderMenuDropdown } from './dropdown';
import usePopup from '../../feature/popup';
import { useOutsideAlerter } from '../../../hooks/click-outside';
import { useTheme } from '../../hooks/use-theme';
import useNavEngagement from '../../feature/nav-engagement';
import { DarkSurfaceProvider } from './dark-surface';
import HeaderNav from './header-nav.component';
import { ChevronIcon } from '../icons/сhevron-icon.component';
import { MenuIcon } from '../icons/burger-icon.component';
import { XIcon } from '../icons/x-icon.component';
import { MobileMenu } from './mobile-menu/header-mobile-menu.component';
import { LanguageSelector } from '../language-selector.component';
import { ThemeSelector } from '../theme-selector.component';

export const Header = () => {
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const { isDark } = useTheme();

  const dropdownRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(dropdownRef, () => {
    setIsDropdownVisible(false);
  });
  const { pathname } = useLocation();
  const isNavEngaged = useNavEngagement();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const { isLoading, isAuthenticated, user } = useAuth();
  const { modal, showPopup } = usePopup();

  const showDropdownHandler = () => {
    setIsDropdownVisible(!isDropdownVisible);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= BREAKPOINTS.headerNav) setIsMobileMenuOpen(false);
      if (window.innerWidth < BREAKPOINTS.headerNav) setIsDropdownVisible(false);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const emailPrefix = user?.email ? user.email.split('@')[0] : '';
  const initials = emailPrefix.slice(0, 2).toUpperCase();
  const displayName =
    emailPrefix.length > 12 ? `${emailPrefix.slice(0, 12)}…` : emailPrefix;

  let rightSide: React.ReactNode =
    isAuthenticated === true ? (
      <button
        onClick={showDropdownHandler}
        className={`flex items-center gap-[8px]
        h-[42px] px-[14px] py-[8px] rounded-[32px] border-blue-600
        border ${isDropdownVisible ? 'shadow-[0px_0px_0px_4px_rgba(93,225,255,0.20)]' : 'shadow-[0px_2px_10px_0px_rgba(42,60,68,0.05)]'}
        bg-blue-500 transition-all`}
      >
        <span className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-blue-deep-2 text-white text-[11px] font-semibold leading-none shrink-0">
          {initials}
        </span>
        <span className="text-[13px] font-medium text-blue-deep-2 max-w-[120px] truncate">
          {displayName}
        </span>
        <ChevronIcon direction={isDropdownVisible ? 'up' : 'down'} />
      </button>
    ) : (
      <HeaderButtons />
    );

  if (isLoading) rightSide = null;

  const isDesktop = windowWidth >= BREAKPOINTS.headerNav;

  // The landing reel runs behind the header, which changes two things: the
  // full-width strip holding the bar has to get out of the way, and the bar's
  // own fill thins out so the video reads through it. Everywhere else the strip
  // is the same colour as the page and so invisible, and the bar stays solid.
  // Standby opens on a full-window reel too, so the bar runs over it the same
  // way. Gated on the grant, not just the path: without one that route renders
  // a plain not-found page, and a transparent bar floating over white is
  // unreadable. The access query is the cached ['my-access'] one, so asking
  // here costs nothing.
  const onStandby = pathname === ROUTER_LINKS.STANDBY;

  // Standby's reel only occupies the first screen. Past it the bar is over
  // ordinary page content, where a transparent fill is simply unreadable — so
  // the transparency is tied to the reel still being behind it, not to the
  // route. The landing page needs no such test: its reel is the whole page.
  const [overReel, setOverReel] = useState(true);
  useEffect(() => {
    if (!onStandby) return undefined;

    // Coalesced to one measurement per frame. Scroll fires far more often than
    // the screen refreshes, and this bar carries a backdrop-blur — cheap to
    // read, expensive to re-evaluate over a playing video.
    let queued = false;
    const measure = () => {
      queued = false;
      setOverReel(window.scrollY < window.innerHeight * 0.72);
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [onStandby]);

  const isOverBackdrop =
    pathname === ROUTER_LINKS.ROOT || (onStandby && overReel);

  const barShape = isDesktop
    ? 'relative flex justify-between items-center h-[74px] rounded-[200px] border border-blue-100 shadow-[0px_2px_10px_0px_rgba(42,60,68,0.05)] w-full max-w-[1296px] px-[16px] py-[16px] transition duration-300 shadow'
    : 'relative flex justify-between items-center h-[76px] w-full border-none rounded-[22px] pr-[26px] p-[12px] transition duration-300';

  // Shape, border and shadow are the shipped values either way — only the fill
  // changes. Over the reel it sits at a quarter opacity and thickens to three
  // quarters while the nav is in use, so the menus are read against something
  // closer to a surface. Both are tokens, so the dark theme thins its own
  // near-black rather than dropping a white bar onto it.
  const translucentFill = isNavEngaged
    ? 'bg-surface-translucent-strong'
    : 'bg-surface-translucent';

  const barSurface = isOverBackdrop
    ? `${translucentFill} backdrop-blur-[20px] backdrop-saturate-150 transition-colors duration-500`
    : 'bg-surface bg-surface-base';

  const headerStyles = `${barShape} ${barSurface}`;

  // Fixed over the reel, sticky everywhere else. Sticky only holds an element
  // within its own containing block, and on the landing page that block is
  // just the header's height — so the moment anything scrolls, a sticky bar
  // leaves with it. Fixed has no such dependency, and costs nothing here
  // because the page beneath it is a fixed backdrop with no flow to disturb.
  const shellStyles = [
    isOverBackdrop
      ? 'fixed top-0 left-0 right-0 z-50 flex justify-center'
      : 'sticky top-0 z-50 w-full flex justify-center',
    isOverBackdrop ? 'bg-transparent' : 'bg-surface-base',
    isDesktop ? 'pt-[8px] px-[20px]' : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Over the LANDING reel the bar is always dark, whichever theme the visitor
  // is on, because that footage is dark. Almost everything in here takes its
  // colour from custom properties, so stamping the attribute re-themes the
  // subtree — including the dropdowns, which render inside it. The theme
  // selector still reports and toggles the real theme; only the surface is
  // being overridden.
  //
  // Standby must NOT do that. Its film comes in a light cut and a dark cut and
  // follows the visitor's theme, so forcing the bar dark put white text over
  // white footage and made the nav unreadable. Leaving it unforced means the
  // bar themes with the page, which is the same thing the film does — dark
  // text on the light cut, light text on the dark one.
  const forceDarkBar = pathname === ROUTER_LINKS.ROOT;
  const onDarkSurface = forceDarkBar || isDark;
  const forcedTheme = forceDarkBar ? 'dark' : undefined;

  return (
    <DarkSurfaceProvider value={onDarkSurface}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-surface-base focus:text-content-primary-alt focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium"
      >
        {t('header.skipToContent')}
      </a>
      {showPopup && modal}
      {/* data-site-header is a hook, not a style: the landing reel watches for
          pointer and focus landing in here so it can drop to its static plate.
          Nothing about the header's own behaviour depends on it. */}
      <div data-site-header data-theme={forcedTheme} className={shellStyles}>
        <div className={headerStyles} ref={dropdownRef}>
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center gap-[46px]">
              <Link to={ROUTER_LINKS.ROOT}>
                <LogoIcon aria-label="Squarebit" />
              </Link>
              {windowWidth >= BREAKPOINTS.headerNav && <HeaderNav />}
            </div>

            <div className="flex items-center gap-[12px]">
              <ThemeSelector />
              {windowWidth >= BREAKPOINTS.headerNav ? (
                <>
                  <LanguageSelector direction="down" />
                  {rightSide}
                  {isDropdownVisible && <HeaderMenuDropdown onClose={showDropdownHandler} />}
                </>
              ) : (
                <button
                  onClick={toggleMobileMenu}
                  aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-menu"
                  className={`transition-transform duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'rotate-90 scale-110' : 'rotate-0'
                  }`}
                >
                  {isMobileMenuOpen ? <XIcon /> : <MenuIcon />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* The panel renders outside the bar but is the same nav, so it gets the
          same forced surface — a light sheet dropping out of a dark bar would
          read as two different components. */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={toggleMobileMenu}
        forcedTheme={forcedTheme}
      />
    </DarkSurfaceProvider>
  );
};
